import dayjs from "dayjs";
import { useEffect, useMemo, useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useDispatch } from "react-redux";
import { useGetLoggedInUserDetails } from "../../../../../../../../aAdminTabTeam2/hooksAndFunctions/customHooks/useGetLoggedInUserDetails";
import { IdNameList } from "../../../../../../../../api/services/common/models";
import {
  useGetCurrencyConversion,
  useGetExpenseCategory,
  useGetExpenseTypeByCategory,
  useGetFeeType,
} from "../../../../../../../../api/services/program/programApis";
import { useGetUsersDetailByRoleMutation } from "../../../../../../../../api/services/user/userApi";
import {
  FormController,
  MuiBox,
  MuiDateFieldFormController,
  MuiGrid,
  MuiLink,
  MuiSelect,
  MuiTextField,
  MuiTypography,
  OptionsDataType,
} from "../../../../../../../../components";
import {
  CURRENCY_FORMAT_MAP,
  CurrencyFormat,
} from "../../../../../../../../components/textFieldCurrency/CurrencyUtils";
import { TextFieldCurrency } from "../../../../../../../../components/textFieldCurrency/TextFieldCurrency";
import {
  DATE_FORMAT,
  DEFAULT_TOOLTIPS_BILLING_AMOUNT,
  DEFAULT_ZERO_VALUE_WITH_TWO_DECIMAL,
  EXCHANGE_RATE_FIELD_MAX_DIGITS_ALLOWED,
  MAX_LENGTH_100,
  MAX_LENGTH_1000,
  ROLE_IDS,
  gridSize,
} from "../../../../../../../../helpers/constants";
import { getDateFormatByCountry } from "../../../../../../../../helpers/constants/countryDateFormat";
import { commonSiteCoreKeys } from "../../../../../../../../helpers/constants/siteCoreKeys";
import {
  convertFirstLastAndIdToOptions,
  formatDecimalWithPrecision,
  getFormattedAmount,
  getNameByIdFromList,
  isEmpty,
  isNotEmpty,
  replaceHashWithValue,
  setApiDataToDropDownOption,
} from "../../../../../../../../helpers/utils";
import { useNavigationSiteCore } from "../../../../../../../../hooks/useNavigationSiteCore";
import { useSiteCore } from "../../../../../../../../hooks/useSiteCore";
import { toggleLoader } from "../../../../../../../../redux/slices/appState/appStateSlice";
import {
  ADD_PROJECT_FEE_AND_EXPENSE_TYPES,
  ProjectAddOrEditExpenseOrFeeFormKey,
} from "../../../../../../modelsAndSchemas/Models";
import { projectAddFeesAndExpensesSiteCoreKeys } from "../../../../../../siteCore/SiteCoreKeys";
import { ProjectExpenseGenericFormWrapper } from "../../ProjectFeesAndExpenseMain.style";
import { GeneralFromExpenseDetailsFields } from "./ProjectGeneralFromExpenseDetailsFields";
interface ProjectAddFeesAndExpenseGeneralFormProps {
  type: string;
  currencyList: IdNameList[] | undefined;
  transactionDateValue?: string;
  feeCurrencyValue?: string;
}

export const ProjectAddFeesAndExpenseGeneralForm = ({
  type,
  currencyList,
  transactionDateValue = "",
  feeCurrencyValue = "",
}: ProjectAddFeesAndExpenseGeneralFormProps) => {
  const { control, getValues, setValue, trigger, clearErrors, getFieldState } =
    useFormContext();

  const { useFetchLabel, commonLabels, fetchValidationMessage } = useSiteCore();
  const { commonARIALabel } = useNavigationSiteCore();
  const [isRateDisabled, setIsRateDisabled] = useState(false);
  const getGeneralFormLabel = useFetchLabel(
    projectAddFeesAndExpensesSiteCoreKeys.addFeesAndExpenses,
  );

  const { loggedInUser } = useGetLoggedInUserDetails();
  const userCountryDateFormat = getDateFormatByCountry(
    loggedInUser?.countryCode ?? "",
  );

  const dispatch = useDispatch();
  // API calls
  const { data: feeTypeApiData, isLoading: isFeeTypeLoading } = useGetFeeType();

  const {
    data: getExpenseCategoryApiData,
    isPending: isExpenseCategoryApiLoading,
  } = useGetExpenseCategory();

  const {
    mutateAsync: getIncurredUsers,
    data: getIncurredUsersApiData,
    isPending: isIncurredUsersApiLoading,
  } = useGetUsersDetailByRoleMutation();
  const {
    data: currencyData,
    isLoading: isCurrencyLoading,
    refetchWithParams: getCurrencyConversion,
    isSuccess: isCurrencySuccess,
    isError: isCurrencyError,
  } = useGetCurrencyConversion();

  useEffect(() => {
    getIncurredUsers({
      roleIds: [ROLE_IDS.projectManager, ROLE_IDS.deliveryCoach],
    });
  }, []);

  const {
    data: getExpenseTypeApiData,
    isLoading: isExpenseTypeApiLoading,
    refetchWithParams: getExpenseTypeByCategoryId,
  } = useGetExpenseTypeByCategory();

  useEffect(() => {
    if (
      isFeeTypeLoading ||
      isExpenseCategoryApiLoading ||
      isIncurredUsersApiLoading ||
      isExpenseTypeApiLoading ||
      isCurrencyLoading
    ) {
      dispatch(
        toggleLoader({
          isVisible: true,
        }),
      );
    } else {
      dispatch(
        toggleLoader({
          isVisible: false,
        }),
      );
    }
    return () => {
      dispatch(toggleLoader({ isVisible: false }));
    };
  }, [
    isFeeTypeLoading,
    isExpenseCategoryApiLoading,
    isIncurredUsersApiLoading,
    isExpenseTypeApiLoading,
    isCurrencyLoading,
  ]);

  const feeOrExpenseCurrencyIdWatch = useWatch({
    control: control,
    name: ProjectAddOrEditExpenseOrFeeFormKey.projectFeeOrExpenseCurrencyId,
  });

  const unitAndQuantityWatch = useWatch({
    control: control,
    disabled: type === ADD_PROJECT_FEE_AND_EXPENSE_TYPES.PROJECT_OR_PROJECT_FEE,
    name: [
      ProjectAddOrEditExpenseOrFeeFormKey.projectExpenseUnitPrice,
      ProjectAddOrEditExpenseOrFeeFormKey.projectExpenseQuantity,
    ],
  });

  useEffect(() => {
    const [expenseUnitPrice, expenseQuantity] = unitAndQuantityWatch;
    let calculatedAmount = 0;
    const expenseUnitPriceFieldState = getFieldState(
      ProjectAddOrEditExpenseOrFeeFormKey.projectExpenseUnitPrice,
    );
    const expenseQuantityFieldState = getFieldState(
      ProjectAddOrEditExpenseOrFeeFormKey.projectExpenseQuantity,
    );
    if (
      expenseUnitPriceFieldState.isDirty ||
      expenseQuantityFieldState.isDirty
    ) {
      if (expenseQuantity && expenseUnitPrice) {
        calculatedAmount = Number(expenseUnitPrice) * Number(expenseQuantity);
      }
      setValue(
        ProjectAddOrEditExpenseOrFeeFormKey.projectFeeOrExpenseAmount,
        String(calculatedAmount),
      );
      setValue(
        ProjectAddOrEditExpenseOrFeeFormKey.projectFeeOrExpenseAmountWithFormat,
        getFormattedAmount(
          calculatedAmount,
          CURRENCY_FORMAT_MAP[feeOrExpenseCurrencyIdWatch]?.locale,
        ),
        {
          shouldDirty: true,
        },
      );
    }
  }, [unitAndQuantityWatch]);

  const billingCurrencyNameWatch = useWatch({
    control: control,
    name: ProjectAddOrEditExpenseOrFeeFormKey.projectBillingCurrencyName,
  });

  const feeOrExpenseCurrencyNameWatch = useWatch({
    control: control,
    name: ProjectAddOrEditExpenseOrFeeFormKey.projectFeeOrExpenseCurrencyName,
  });

  const exchangeRateWatch = useWatch({
    control,
    name: ProjectAddOrEditExpenseOrFeeFormKey.projectBillingCurrencyExchangeRate,
  });

  const feeOrExpenseAmountWatch = useWatch({
    control,
    name: ProjectAddOrEditExpenseOrFeeFormKey.projectFeeOrExpenseAmount,
  });

  const feeOrExpenseCurrencyWatch = useWatch({
    control,
    name: ProjectAddOrEditExpenseOrFeeFormKey.projectFeeOrExpenseCurrencyId,
  });
  const transactionDateWatch = useWatch({
    control,
    name: ProjectAddOrEditExpenseOrFeeFormKey.projectTransactionDate,
  });

  const isProjectProjectFeeType = useMemo(() => {
    return type !== ADD_PROJECT_FEE_AND_EXPENSE_TYPES.PROJECT_EXPENSE;
  }, [type]);

  const expenseCategoryIdWatch = useWatch({
    control,
    name: ProjectAddOrEditExpenseOrFeeFormKey.projectExpenseCategoryId,
    disabled: isProjectProjectFeeType,
  });

  useEffect(() => {
    if (expenseCategoryIdWatch) {
      getExpenseTypeByCategoryId(expenseCategoryIdWatch);
    }
  }, [expenseCategoryIdWatch]);

  const billingCurrencyFormat = useMemo(() => {
    const currencyFormat: CurrencyFormat =
      CURRENCY_FORMAT_MAP[billingCurrencyNameWatch];
    return currencyFormat?.locale;
  }, [billingCurrencyNameWatch]);

  const feeOrExpenseCurrencyFormat = useMemo(() => {
    const currencyName = getNameByIdFromList(
      currencyList,
      feeOrExpenseCurrencyWatch,
    );
    setValue(
      ProjectAddOrEditExpenseOrFeeFormKey.projectFeeOrExpenseCurrencyName,
      currencyName,
    );
    const currencyFormat: CurrencyFormat = CURRENCY_FORMAT_MAP[currencyName];
    return currencyFormat?.locale;
  }, [feeOrExpenseCurrencyWatch]);

  // Dropdown value coming from BE API
  // FeeTypes List
  const feeTypeDropdownOptions = useMemo(() => {
    if (feeTypeApiData?.length) {
      return setApiDataToDropDownOption(feeTypeApiData) as OptionsDataType[];
    }
    return [];
  }, [feeTypeApiData]);

  // Expense Category List
  const updateCategoryDropdownOptions = useMemo(() => {
    if (getExpenseCategoryApiData?.length) {
      return setApiDataToDropDownOption(
        getExpenseCategoryApiData,
      ) as OptionsDataType[];
    }
    return [];
  }, [getExpenseCategoryApiData]);

  // Incurred By Users List
  const updateIncurredByUserDropdownOptions = useMemo(() => {
    if (getIncurredUsersApiData?.length) {
      return convertFirstLastAndIdToOptions(getIncurredUsersApiData);
    }
    return [];
  }, [getIncurredUsersApiData]);

  // expense types by category
  const updateExpenseTypeDropdown = useMemo(() => {
    if (getExpenseTypeApiData?.length) {
      return setApiDataToDropDownOption(
        getExpenseTypeApiData,
      ) as OptionsDataType[];
    }
    return [];
  }, [getExpenseTypeApiData]);

  // Currency List
  const updateCurrencySelectOptions = useMemo(() => {
    if (currencyList?.length) {
      return setApiDataToDropDownOption(currencyList) as OptionsDataType[];
    }
    return [];
  }, [currencyList]);

  const checkIsExpenseOrFeeCurrencyMismatch = useMemo(() => {
    const billingCurrencyId = getValues(
      ProjectAddOrEditExpenseOrFeeFormKey.projectBillingCurrency,
    );
    const currencyId = feeOrExpenseCurrencyIdWatch;
    if (
      currencyId &&
      currencyId !== billingCurrencyId &&
      transactionDateWatch
    ) {
      return true;
    }
    return false;
  }, [feeOrExpenseCurrencyIdWatch, transactionDateWatch]);

  useEffect(() => {
    const isFeeCurrencyFieldDirty = getFieldState(
      ProjectAddOrEditExpenseOrFeeFormKey.projectFeeOrExpenseCurrencyId,
    ).isDirty;
    if (isFeeCurrencyFieldDirty && checkIsExpenseOrFeeCurrencyMismatch) {
      resetIsRateConvertedValue();
      resetExchangeBillingValue();
    }
  }, [feeOrExpenseCurrencyIdWatch, checkIsExpenseOrFeeCurrencyMismatch]);

  useEffect(() => {
    if (!checkIsExpenseOrFeeCurrencyMismatch) {
      clearErrorExchangeRate();
      setValue(
        ProjectAddOrEditExpenseOrFeeFormKey.projectBillingCurrencyExchangeRate,
        "",
      );
      const amount =
        getValues(
          ProjectAddOrEditExpenseOrFeeFormKey.projectFeeOrExpenseAmount,
        ) ?? "0";
      setValue(
        ProjectAddOrEditExpenseOrFeeFormKey.projectBillingAmount,
        amount,
      );
    }
  }, [checkIsExpenseOrFeeCurrencyMismatch]);

  const forceToValidateExchangeRateError = () => {
    trigger(
      ProjectAddOrEditExpenseOrFeeFormKey.projectBillingCurrencyExchangeRate,
    );
  };

  const resetExchangeBillingValue = () => {
    if (checkIsExpenseOrFeeCurrencyMismatch) {
      setValue(
        ProjectAddOrEditExpenseOrFeeFormKey.projectBillingAmountWithFormat,
        DEFAULT_ZERO_VALUE_WITH_TWO_DECIMAL,
      );
    }
  };

  const resetIsRateConvertedValue = () => {
    setValue(ProjectAddOrEditExpenseOrFeeFormKey.projectIsRateConverted, false);
  };

  const clearErrorExchangeRate = () => {
    clearErrors(
      ProjectAddOrEditExpenseOrFeeFormKey.projectBillingCurrencyExchangeRate,
    );
  };

  useEffect(() => {
    const isFeeAmountWithFormatFieldDirty = getFieldState(
      ProjectAddOrEditExpenseOrFeeFormKey.projectFeeOrExpenseAmountWithFormat,
    ).isDirty;
    if (isFeeAmountWithFormatFieldDirty) {
      resetIsRateConvertedValue();
      resetExchangeBillingValue();
    }
  }, [feeOrExpenseAmountWatch, feeOrExpenseCurrencyFormat]);

  useEffect(() => {
    const isExchangeRateWithFormatFieldDirty = getFieldState(
      ProjectAddOrEditExpenseOrFeeFormKey.projectBillingCurrencyExchangeRate,
    ).isDirty;
    if (isExchangeRateWithFormatFieldDirty) {
      resetExchangeBillingValue();
    }
  }, [exchangeRateWatch]);

  useEffect(() => {
    if (isCurrencySuccess && isNotEmpty(currencyData)) {
      setIsRateDisabled(isNotEmpty(currencyData));
      setValue(
        ProjectAddOrEditExpenseOrFeeFormKey.projectBillingCurrencyExchangeRate,
        currencyData[0]?.conversionRate,
      );
    }
  }, [isCurrencySuccess, currencyData]);

  useEffect(() => {
    if (
      isCurrencySuccess &&
      isEmpty(currencyData) &&
      feeOrExpenseCurrencyWatch !== feeCurrencyValue
    ) {
      setValue(
        ProjectAddOrEditExpenseOrFeeFormKey.projectBillingCurrencyExchangeRate,
        "",
      );
    }
  }, [isCurrencySuccess, feeOrExpenseCurrencyWatch]);

  useEffect(() => {
    if (isCurrencyError) {
      setValue(
        ProjectAddOrEditExpenseOrFeeFormKey.projectBillingCurrencyExchangeRate,
        "",
      );
    }
  }, [isCurrencyError]);

  useEffect(() => {
    if (transactionDateWatch && feeOrExpenseCurrencyWatch) {
      if (
        transactionDateWatch ===
          dayjs(transactionDateValue).format(userCountryDateFormat) &&
        feeOrExpenseCurrencyWatch === feeCurrencyValue
      ) {
        setIsRateDisabled(true);
        return;
      }

      setIsRateDisabled(false);
      const transactionDateFormat = dayjs(
        transactionDateWatch,
        userCountryDateFormat,
      ).format(DATE_FORMAT.YYYY_MM_DD_HYPHEN);
      getCurrencyConversion(
        transactionDateFormat,
        feeOrExpenseCurrencyWatch,
        getValues(ProjectAddOrEditExpenseOrFeeFormKey.projectBillingCurrency),
      );
    }
  }, [
    transactionDateWatch,
    feeOrExpenseCurrencyWatch,
    checkIsExpenseOrFeeCurrencyMismatch,
  ]);

  useEffect(() => {
    if (feeOrExpenseCurrencyFormat && !checkIsExpenseOrFeeCurrencyMismatch) {
      setValue(
        ProjectAddOrEditExpenseOrFeeFormKey.projectBillingAmount,
        feeOrExpenseAmountWatch,
      );
      let billingAmount = Number(feeOrExpenseAmountWatch);

      const formattedAmount =
        feeOrExpenseAmountWatch &&
        `${getFormattedAmount(billingAmount, billingCurrencyFormat)}`;
      setValue(
        ProjectAddOrEditExpenseOrFeeFormKey.projectBillingAmountWithFormat,
        formattedAmount,
      );
      setValue(
        ProjectAddOrEditExpenseOrFeeFormKey.projectBillingAmount,
        String(billingAmount),
      );
    }
  }, [feeOrExpenseAmountWatch, feeOrExpenseCurrencyFormat]);

  const handleConvertRate = () => {
    setValue(ProjectAddOrEditExpenseOrFeeFormKey.projectIsRateConverted, true);
    clearErrorExchangeRate();
    forceToValidateExchangeRateError();

    const amount = Number(
      getValues(
        ProjectAddOrEditExpenseOrFeeFormKey.projectFeeOrExpenseAmount,
      ) ?? "0",
    );
    const exchangeRate = Number(
      getValues(
        ProjectAddOrEditExpenseOrFeeFormKey.projectBillingCurrencyExchangeRate,
      ) ?? "0",
    );
    const convertedAmount = amount ? amount * exchangeRate : 0;
    const formatExchangeRate = convertedAmount.toFixed(2);
    setValue(
      ProjectAddOrEditExpenseOrFeeFormKey.projectBillingAmount,
      String(formatExchangeRate),
    );
    const formattedAmount = `${getFormattedAmount(
      formatExchangeRate,
      billingCurrencyFormat,
    )}`;
    setValue(
      ProjectAddOrEditExpenseOrFeeFormKey.projectBillingAmountWithFormat,
      formattedAmount,
    );
  };

  const exchangeRateTooltipsValue = () => {
    return replaceHashWithValue(
      getGeneralFormLabel(
        projectAddFeesAndExpensesSiteCoreKeys.billingDetailsTooltipMessage,
      ),
      {
        BILLINGCURRENCY: billingCurrencyNameWatch,
        FEECURRENCY: feeOrExpenseCurrencyNameWatch,
      },
    );
  };

  const billingAmountTooltipsValue = () => {
    return replaceHashWithValue(
      getGeneralFormLabel(
        projectAddFeesAndExpensesSiteCoreKeys.billingDetailsAmountTooltip,
      ),
      {
        BILLINGCURRENCY: billingCurrencyNameWatch,
        EXCHANGERATE: exchangeRateWatch
          ? exchangeRateWatch
          : DEFAULT_TOOLTIPS_BILLING_AMOUNT,
      },
    );
  };

  const renderCurrencyAmountFields = () => {
    return (
      <>
        <MuiGrid size={gridSize} data-testid="currency-select-field">
          <FormController
            control={control}
            name={
              ProjectAddOrEditExpenseOrFeeFormKey.projectFeeOrExpenseCurrencyId
            }
          >
            <MuiSelect
              isMandatory
              label={getGeneralFormLabel(
                isProjectProjectFeeType
                  ? projectAddFeesAndExpensesSiteCoreKeys.feeDetailsCurrency
                  : projectAddFeesAndExpensesSiteCoreKeys.expenseDetailsCurrency,
              )}
              showSearch
              options={updateCurrencySelectOptions}
              visibleDefaultSelectOption
              placeholder={commonLabels.selectPlaceHolder}
              requiredARIALabel={commonARIALabel.required}
            />
          </FormController>
        </MuiGrid>

        <MuiGrid size={gridSize}>
          <FormController
            control={control}
            name={
              ProjectAddOrEditExpenseOrFeeFormKey.projectFeeOrExpenseAmountWithFormat
            }
          >
            <TextFieldCurrency
              id={
                ProjectAddOrEditExpenseOrFeeFormKey.projectFeeOrExpenseAmountWithFormat
              }
              isMandatory
              label={getGeneralFormLabel(
                isProjectProjectFeeType
                  ? projectAddFeesAndExpensesSiteCoreKeys.feeDetailsAmount
                  : projectAddFeesAndExpensesSiteCoreKeys.expenseDetailsAmount,
              )}
              disabled={!isProjectProjectFeeType}
              isCurrencyChanged={feeOrExpenseCurrencyFormat}
              formId={
                ProjectAddOrEditExpenseOrFeeFormKey.projectFeeOrExpenseAmountWithFormat
              }
              locale={feeOrExpenseCurrencyFormat}
              zeroValidationMessage={fetchValidationMessage(
                commonSiteCoreKeys.greaterThanZeroFieldValidator,
              )}
              formatErrorMessage={fetchValidationMessage(
                commonSiteCoreKeys.alphabetNotAllowedValidator,
              )}
              name={
                ProjectAddOrEditExpenseOrFeeFormKey.projectFeeOrExpenseAmountWithFormat
              }
              placeholder={
                isProjectProjectFeeType
                  ? commonLabels.inputPlaceHolder
                  : DEFAULT_ZERO_VALUE_WITH_TWO_DECIMAL
              }
              getFinalValues={(_value, valueNonFormatted) => {
                setValue(
                  ProjectAddOrEditExpenseOrFeeFormKey.projectFeeOrExpenseAmountWithFormat,
                  _value,
                );
                setValue(
                  ProjectAddOrEditExpenseOrFeeFormKey.projectFeeOrExpenseAmount,
                  valueNonFormatted,
                  {
                    shouldTouch: true,
                  },
                );
              }}
              requiredARIALabel={commonARIALabel.required}
            />
          </FormController>
        </MuiGrid>
      </>
    );
  };

  const handleOnChangeExchangeRate = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const value = e.target.value;
    if (value) {
      const formattedExchangeRateValue = formatDecimalWithPrecision(
        value,
        EXCHANGE_RATE_FIELD_MAX_DIGITS_ALLOWED,
      );
      if (formattedExchangeRateValue !== value) {
        setValue(
          ProjectAddOrEditExpenseOrFeeFormKey.projectBillingCurrencyExchangeRate,
          formattedExchangeRateValue,
        );
      }
    }
  };

  return (
    <ProjectExpenseGenericFormWrapper>
      {/* general field */}
      <MuiGrid container spacing={2} className="form-section-general">
        <MuiGrid size={gridSize}>
          <FormController
            control={control}
            name={ProjectAddOrEditExpenseOrFeeFormKey.projectFeeOrExpenseName}
          >
            <MuiTextField
              id={ProjectAddOrEditExpenseOrFeeFormKey.projectFeeOrExpenseName}
              label={getGeneralFormLabel(
                isProjectProjectFeeType
                  ? projectAddFeesAndExpensesSiteCoreKeys.feeName
                  : projectAddFeesAndExpensesSiteCoreKeys.expenseName,
              )}
              required
              isMandatory
              placeholder={commonLabels.inputPlaceHolder}
              maxLength={MAX_LENGTH_100}
              showCloseIcon
              requiredARIALabel={commonARIALabel.required}
            />
          </FormController>
        </MuiGrid>
        <MuiGrid size={gridSize}>
          <MuiDateFieldFormController
            control={control}
            isMandatory
            className="date-field"
            label={getGeneralFormLabel(
              projectAddFeesAndExpensesSiteCoreKeys.transactionDate,
            )}
            name={ProjectAddOrEditExpenseOrFeeFormKey.projectTransactionDate}
            format={userCountryDateFormat}
            valueFormat={userCountryDateFormat}
            value={
              getValues(
                ProjectAddOrEditExpenseOrFeeFormKey.projectTransactionDate,
              ) !== ""
                ? dayjs(
                    getValues(
                      ProjectAddOrEditExpenseOrFeeFormKey.projectTransactionDate,
                    ),
                    userCountryDateFormat,
                  )
                : null
            }
            requiredARIALabel={commonARIALabel.required}
            selectDateARIALabel={commonARIALabel.selectDate}
            id={`${ProjectAddOrEditExpenseOrFeeFormKey.projectTransactionDate}-field`}
          />
        </MuiGrid>
        {!isProjectProjectFeeType && (
          <MuiGrid size={gridSize}>
            <FormController
              control={control}
              name={
                ProjectAddOrEditExpenseOrFeeFormKey.projectExpenseIncurredById
              }
            >
              <MuiSelect
                label={getGeneralFormLabel(
                  projectAddFeesAndExpensesSiteCoreKeys.incurredBy,
                )}
                options={updateIncurredByUserDropdownOptions}
                visibleDefaultSelectOption
                placeholder={commonLabels.selectPlaceHolder}
                requiredARIALabel={commonARIALabel.required}
              />
            </FormController>
          </MuiGrid>
        )}
      </MuiGrid>
      {/* Fees Section */}
      {isProjectProjectFeeType && (
        <MuiBox className="form-section">
          <MuiBox className="form-section-header">
            <MuiTypography variant="h2SemiBold">
              {getGeneralFormLabel(
                projectAddFeesAndExpensesSiteCoreKeys.feeDetailsHeading,
              )}
            </MuiTypography>
          </MuiBox>
          <MuiGrid container spacing={2}>
            <MuiGrid size={gridSize}>
              <FormController
                control={control}
                name={ProjectAddOrEditExpenseOrFeeFormKey.projectFeeTypeId}
              >
                <MuiSelect
                  id={ProjectAddOrEditExpenseOrFeeFormKey.projectFeeTypeId}
                  label={getGeneralFormLabel(
                    projectAddFeesAndExpensesSiteCoreKeys.feeDetailsType,
                  )}
                  showSearch
                  options={feeTypeDropdownOptions}
                  visibleDefaultSelectOption
                  placeholder={commonLabels.selectPlaceHolder}
                  isMandatory
                  requiredARIALabel={commonARIALabel.required}
                />
              </FormController>
            </MuiGrid>
            {renderCurrencyAmountFields()}
          </MuiGrid>
        </MuiBox>
      )}
      {/* Expense Section */}
      {!isProjectProjectFeeType && (
        <GeneralFromExpenseDetailsFields
          categoryOptions={updateCategoryDropdownOptions}
          typeOptions={updateExpenseTypeDropdown}
        >
          {renderCurrencyAmountFields()}
        </GeneralFromExpenseDetailsFields>
      )}

      {/* Billing details */}
      <MuiBox className="form-section">
        <MuiBox className="form-section-header">
          <MuiTypography variant="h2SemiBold">
            {getGeneralFormLabel(
              projectAddFeesAndExpensesSiteCoreKeys.billingDetailsHeading,
            )}
          </MuiTypography>
        </MuiBox>
        <MuiGrid container spacing={2}>
          <MuiGrid size={gridSize}>
            <FormController
              control={control}
              name={
                ProjectAddOrEditExpenseOrFeeFormKey.projectBillingCurrencyName
              }
            >
              <MuiTextField
                label={getGeneralFormLabel(
                  projectAddFeesAndExpensesSiteCoreKeys.billingDetailsCurrency,
                )}
                disabled
                placeholder={commonLabels.inputPlaceHolder}
                showCloseIcon
                requiredARIALabel={commonARIALabel.required}
              />
            </FormController>
          </MuiGrid>
          {checkIsExpenseOrFeeCurrencyMismatch && (
            <MuiGrid size={gridSize} className="exchange-rate-input">
              <FormController
                control={control}
                name={
                  ProjectAddOrEditExpenseOrFeeFormKey.projectBillingCurrencyExchangeRate
                }
              >
                <MuiTextField
                  id={
                    ProjectAddOrEditExpenseOrFeeFormKey.projectBillingCurrencyExchangeRate
                  }
                  isMandatory
                  name={
                    ProjectAddOrEditExpenseOrFeeFormKey.projectBillingCurrencyExchangeRate
                  }
                  label={getGeneralFormLabel(
                    projectAddFeesAndExpensesSiteCoreKeys.billingDetailsExchangeRate,
                  )}
                  infoMessage={exchangeRateTooltipsValue()}
                  placeholder={commonLabels.inputPlaceHolder}
                  onBlur={resetIsRateConvertedValue}
                  type="number"
                  positiveValuesOnly
                  onChange={handleOnChangeExchangeRate}
                  disabled={isRateDisabled}
                  requiredARIALabel={commonARIALabel.required}
                />
              </FormController>
              <MuiLink
                dataTestId="convert-link"
                className="convert-link"
                onClick={handleConvertRate}
              >
                {getGeneralFormLabel(
                  projectAddFeesAndExpensesSiteCoreKeys.billingDetailsConvert,
                )}
              </MuiLink>
            </MuiGrid>
          )}
          <MuiGrid size={gridSize}>
            <FormController
              control={control}
              name={
                ProjectAddOrEditExpenseOrFeeFormKey.projectBillingAmountWithFormat
              }
            >
              <MuiTextField
                id={
                  ProjectAddOrEditExpenseOrFeeFormKey.projectBillingAmountWithFormat
                }
                isMandatory
                disabled
                label={getGeneralFormLabel(
                  projectAddFeesAndExpensesSiteCoreKeys.billingDetailsAmount,
                )}
                infoMessage={billingAmountTooltipsValue()}
                placeholder={DEFAULT_ZERO_VALUE_WITH_TWO_DECIMAL}
                requiredARIALabel={commonARIALabel.required}
              />
            </FormController>
          </MuiGrid>
        </MuiGrid>
      </MuiBox>
      {/* Comment section */}
      <MuiGrid container columnGap={3} className="form-section">
        <MuiGrid size={{ md: 8, sm: 8, xs: 12 }}>
          <FormController
            control={control}
            name={ProjectAddOrEditExpenseOrFeeFormKey.projectComments}
          >
            <MuiTextField
              label={commonLabels.comment}
              placeholder={commonLabels.inputPlaceHolder}
              isTextarea
              multiline
              rows={4}
              maxLength={MAX_LENGTH_1000}
              requiredARIALabel={commonARIALabel.required}
            />
          </FormController>
        </MuiGrid>
      </MuiGrid>
    </ProjectExpenseGenericFormWrapper>
  );
};
